<template>
  <div class="phone-numbers-card">
    <div class="section-header" @click="toggleSection">
      <i
        :class="['mdi', isExpanded ? 'mdi-chevron-down' : 'mdi-chevron-right']"
      ></i>
      <span>Клієнти</span>
      <div class="header-actions" @click.stop>
        <span class="phone-count">{{ clients.length }}</span>
        <button
          class="add-phone-btn"
          @click="showAddModal = true"
          title="Додати номер"
        >
          <i class="mdi mdi-plus"></i>
        </button>
      </div>
    </div>

    <div v-if="isExpanded" class="phone-list">
      <div
        v-for="(phone, index) in clients"
        :key="index"
        :class="['phone-item', { 'menu-active': activeMenuIndex === index }]"
        @click.self="handlePhoneClick(phone)"
      >
        <div class="phone-icon bg-soft-info" @click="handlePhoneClick(phone)">
          <span>{{ index + 1 }}</span>
        </div>

        <div class="phone-content" @click="handlePhoneClick(phone)">
          <div class="phone-number">+{{ formatPhoneNumber(phone.number) }}</div>
          <div class="phone-company">
            <!-- <i class="mdi mdi-office-building"></i> -->
            {{ phone.companyName || "Компанія не додана" }}
          </div>
        </div>

        <div
          class="phone-stats"
          v-if="phone.appealsStat && phone.appealsStat.count"
        >
          <div class="stats-info">
            <div class="stats-badge">
              <i class="mdi mdi-message-text"></i>
              <span>{{ phone.appealsStat.count }}</span>
            </div>
            <div class="stats-date">
              {{ formatDateTime(phone.appealsStat) }}
            </div>
          </div>
        </div>

        <div class="phone-actions">
          <div class="action-menu-wrapper">
            <button
              class="action-btn action-btn-menu"
              @click.stop="toggleMenu(index)"
              title="Опції"
            >
              <i class="mdi mdi-dots-vertical"></i>
            </button>
            <div v-if="activeMenuIndex === index" class="action-dropdown">
              <div
                class="action-dropdown-item"
                @click.stop="copyPhoneNumber(phone.number)"
              >
                <i class="mdi mdi-content-copy"></i>
                <span>Копіювати номер телефону</span>
              </div>
              <div
                class="action-dropdown-item"
                @click.stop="copyCompanyId(phone.companyId)"
              >
                <i class="mdi mdi-card-account-details"></i>
                <span>Копіювати ID компанії</span>
              </div>
              <div class="action-dropdown-divider"></div>
              <div
                class="action-dropdown-item"
                @click.stop="handleRepeatAppeal(phone.number)"
              >
                <i class="mdi mdi-refresh"></i>
                <span>Повторне звернення</span>
              </div>
              <div
                class="action-dropdown-item"
                @click.stop="handleViewAppeals(phone.number)"
              >
                <i class="mdi mdi-eye"></i>
                <span>Перегляд звернень</span>
              </div>
              <div class="action-dropdown-divider"></div>
              <div
                class="action-dropdown-item action-dropdown-item-danger"
                @click.stop="handleDelete(phone, index)"
              >
                <i class="mdi mdi-delete"></i>
                <span>Видалити</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="clients.length === 0" class="empty-state">
        <i class="mdi mdi-phone-off"></i>
        <p>Немає клієнтів</p>
      </div>
    </div>

    <!-- Модальне вікно для додавання номера -->
    <teleport to="body" v-if="showAddModal">
      <div class="modal-overlay" @click.self="closeAddModal">
        <div class="modal-container">
          <div class="modal-header">
            <div class="modal-header-content">
              <i class="mdi mdi-plus-circle"></i>
              <h3>Додати новий номер</h3>
            </div>
            <button class="modal-close" @click="closeAddModal">
              <i class="mdi mdi-close"></i>
            </button>
          </div>

          <div class="modal-body">
            <div class="form-group">
              <label>Номер телефону</label>
              <div class="phone-input-wrapper-with-tag">
                <button
                  v-if="userPhone"
                  class="my-phone-tag"
                  @click="useMyPhone"
                  title="Підставити мій номер"
                >
                  <i class="mdi mdi-account"></i>
                  Мій номер
                </button>
                <div class="phone-input-wrapper">
                  <input
                    v-model="newPhone.number"
                    type="text"
                    class="form-input"
                    placeholder="+380631112233"
                    @keyup.enter="handleSearchByEnter"
                  />
                  <button
                    class="search-company-btn"
                    @click="findCompanies(newPhone.number)"
                    :disabled="!canSearch"
                    :title="
                      canSearch
                        ? 'Знайти компанії'
                        : 'Введіть мінімум 8 цифр для пошуку'
                    "
                  >
                    <i
                      :class="
                        isSearching
                          ? 'mdi mdi-loading mdi-spin'
                          : 'mdi mdi-magnify'
                      "
                    ></i>
                  </button>
                </div>
              </div>
            </div>

            <div class="form-group">
              <label>
                Виберіть компанію
                <span v-if="foundCompanies.length > 0" class="companies-count">
                  (знайдено: {{ filteredCompanies.length }} з
                  {{ foundCompanies.length }})
                </span>
              </label>

              <!-- Поле пошуку по компаніях -->
              <div
                v-if="foundCompanies.length > 0 && !isSearching"
                class="company-search-input-wrapper"
              >
                <i class="mdi mdi-magnify"></i>
                <input
                  v-model="companySearchQuery"
                  type="text"
                  class="company-search-input"
                  placeholder="Пошук по назві компанії..."
                />
                <button
                  v-if="companySearchQuery"
                  class="clear-search-btn"
                  @click="companySearchQuery = ''"
                  title="Очистити пошук"
                >
                  <i class="mdi mdi-close"></i>
                </button>
              </div>

              <div v-if="isSearching" class="company-search-loading">
                <div
                  class="spinner-border spinner-border-sm"
                  role="status"
                ></div>
                <span>Пошук компаній...</span>
              </div>
              <div v-else-if="foundCompanies.length > 0">
                <div
                  v-if="filteredCompanies.length === 0"
                  class="company-not-found"
                >
                  <i class="mdi mdi-magnify"></i>
                  <p>
                    Не знайдено компаній за запитом "{{ companySearchQuery }}"
                  </p>
                  <button
                    class="btn btn-clear-filter"
                    @click="companySearchQuery = ''"
                  >
                    <i class="mdi mdi-close"></i>
                    Очистити фільтр
                  </button>
                </div>
                <div v-else class="company-list">
                  <div
                    v-for="company in filteredCompanies"
                    :key="company.companyId"
                    :class="[
                      'company-item',
                      { selected: newPhone.companyId === company.companyId },
                      { blocked: company.blocked },
                    ]"
                    @click="selectFoundCompany(company)"
                  >
                    <div class="company-info">
                      <div class="company-header">
                        <div class="company-name">
                          {{ company.companyName }}
                          <i
                            v-if="company.personAdmin"
                            class="mdi mdi-crown company-admin-badge"
                            title="Ви адміністратор цієї компанії"
                          ></i>
                          <span
                            v-if="company.blocked"
                            class="company-blocked-badge"
                            title="Компанія заблокована"
                          >
                            <i class="mdi mdi-lock"></i>
                            Заблокована
                          </span>
                        </div>
                        <div class="company-code">
                          ID: {{ company.companyId }}
                        </div>
                      </div>
                      <div
                        v-if="company.companyDescription"
                        class="company-description"
                      >
                        {{ company.companyDescription }}
                      </div>
                      <div v-if="company.more" class="company-meta">
                        <span
                          :class="[
                            'company-version-badge',
                            getVersionClass(company.more.companyServer),
                          ]"
                        >
                          <i
                            :class="getVersionIcon(company.more.companyServer)"
                          ></i>
                          {{ getVersionLabel(company.more.companyServer) }}
                        </span>
                        <span class="company-server-badge">
                          <i class="mdi mdi-server"></i>
                          {{
                            company.more.serverName ||
                            company.more.companyServer
                          }}
                        </span>
                        <span
                          v-if="company.more.dateOfCreate"
                          class="company-date"
                        >
                          <i class="mdi mdi-calendar"></i>
                          {{ formatDate(company.more.dateOfCreate) }}
                        </span>
                      </div>
                    </div>

                    <i
                      v-if="newPhone.companyId === company.companyId"
                      class="mdi mdi-check-circle company-selected-icon"
                    ></i>
                  </div>
                </div>
              </div>
              <div
                v-else-if="searchPerformed && foundCompanies.length === 0"
                class="company-not-found"
              >
                <i class="mdi mdi-information"></i>
                <p>Компанії не знайдено для цього номера</p>
                <button
                  class="btn btn-add-without-company"
                  @click="addPhoneWithoutCompany"
                >
                  <i class="mdi mdi-plus"></i>
                  Додати номер без компанії
                </button>
              </div>
              <div v-else class="company-hint">
                <i class="mdi mdi-information"></i>
                <p>Введіть номер телефону для пошуку компаній</p>
              </div>
            </div>
          </div>

          <div class="modal-footer">
            <button class="btn btn-cancel" @click="closeAddModal">
              Скасувати
            </button>
            <button
              class="btn btn-primary"
              @click="handleAddPhone"
              :disabled="!isFormValid"
            >
              Додати
            </button>
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script>
import { storeS } from "@/store";
import { SuperAdmin } from "@/API.js";

let apiAdmin = new SuperAdmin();

export default {
  name: "ClientsMiniList",
  props: {
    clients: {
      type: Array,
      default: () => [],
    },
    ticketId: {
      type: [String, Number],
      default: null,
    },
  },
  emits: ["phone-click", "add-phone", "delete-phone", "repeat-appeal", "view-appeals"],
  data() {
    return {
      isExpanded: false,
      activeMenuIndex: null,
      showAddModal: false,
      newPhone: {
        number: "",
        companyId: null,
        companyName: "",
      },
      foundCompanies: [],
      isSearching: false,
      searchPerformed: false,
      companySearchQuery: "",
    };
  },
  computed: {
    userPhone() {
      return storeS.userbase.phone || null;
    },
    isFormValid() {
      return this.newPhone.number && this.newPhone.companyId;
    },
    canSearch() {
      const digitsOnly = this.newPhone.number.replace(/\D/g, "");
      return digitsOnly.length >= 8;
    },
    filteredCompanies() {
      if (!this.companySearchQuery || this.companySearchQuery.trim() === "") {
        return this.foundCompanies;
      }

      const query = this.companySearchQuery.toLowerCase().trim();

      return this.foundCompanies.filter((company) => {
        const companyName = (company.companyName || "").toLowerCase();
        const companyId = String(company.companyId || "").toLowerCase();
        const companyDescription = (
          company.companyDescription || ""
        ).toLowerCase();
        const serverName = (company.more?.serverName || "").toLowerCase();

        return (
          companyName.includes(query) ||
          companyId.includes(query) ||
          companyDescription.includes(query) ||
          serverName.includes(query)
        );
      });
    },
  },
  mounted() {
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
  },
  methods: {
    toggleSection() {
      this.isExpanded = !this.isExpanded;
    },
    handlePhoneClick(phone) {
      this.$emit("phone-click", phone);
    },
    formatPhoneNumber(number) {
      const numStr = String(number);
      if (numStr.length >= 12) {
        return `${numStr.slice(0, 3)} ${numStr.slice(3, 5)} ${numStr.slice(
          5,
          8
        )} ${numStr.slice(8, 10)} ${numStr.slice(10)}`;
      }
      return numStr;
    },
    formatDateTime(appealsStat) {
      if (!appealsStat || !appealsStat.date) return "-";

      const date = appealsStat.date;
      const time = appealsStat.time || "";

      const [year, month, day] = date.split("-");
      const formattedDate = `${day}.${month}.${year}`;

      if (time) {
        const [hours, minutes] = time.split(":");
        return `${formattedDate} ${hours}:${minutes}`;
      }

      return formattedDate;
    },
    copyPhoneNumber(number) {
      const formattedNumber = `+${number}`;
      navigator.clipboard.writeText(formattedNumber).then(() => {
        this.$toast.success("Номер телефону скопійовано: " + formattedNumber);
      });
      this.closeMenu();
    },
    copyCompanyId(companyId) {
      navigator.clipboard.writeText(String(companyId)).then(() => {
        this.$toast.success("ID компанії скопійовано: " + companyId);
      });
      this.closeMenu();
    },
    toggleMenu(index) {
      this.activeMenuIndex = this.activeMenuIndex === index ? null : index;
    },
    handleClickOutside(event) {
      if (event.type !== "click") return;

      const dropdown = document.querySelector(".action-dropdown");
      const menuButton = event.target.closest(".action-btn-menu");
      const isClickInsideDropdown = dropdown && dropdown.contains(event.target);

      if (!menuButton && !isClickInsideDropdown) {
        this.activeMenuIndex = null;
      }
    },
    closeMenu() {
      this.activeMenuIndex = null;
    },
    handleRepeatAppeal(number) {
      this.closeMenu();
      this.$emit("repeat-appeal", number);
    },
    handleViewAppeals(number) {
      this.closeMenu();
      this.$emit("view-appeals", number);
    },
    handleDelete(phone, index) {
      this.closeMenu();
      this.$emit("delete-phone", { phone, index });
    },
    closeAddModal() {
      this.showAddModal = false;
      this.newPhone = {
        number: "",
        companyId: null,
        companyName: "",
      };
      this.foundCompanies = [];
      this.isSearching = false;
      this.searchPerformed = false;
      this.companySearchQuery = "";
    },
    phoneReset(number) {
      let cleanNumber = number.replace(/[^\d]/g, "");

      if (cleanNumber.startsWith("380")) {
        return cleanNumber;
      }

      const numcodeWith0 = [
        "039",
        "067",
        "068",
        "096",
        "097",
        "098",
        "050",
        "066",
        "095",
        "099",
        "063",
        "073",
        "093",
        "091",
        "092",
        "089",
        "094",
      ];

      const numcodeWithout0 = [
        "39",
        "67",
        "68",
        "96",
        "97",
        "98",
        "50",
        "66",
        "95",
        "99",
        "63",
        "73",
        "93",
        "91",
        "92",
        "89",
        "94",
      ];

      let hasZero = false;
      numcodeWith0.forEach((el) => {
        if (cleanNumber.startsWith(el)) {
          hasZero = true;
        }
      });

      if (hasZero) {
        return "38" + cleanNumber;
      }

      let hasCode = false;
      numcodeWithout0.forEach((el) => {
        if (cleanNumber.startsWith(el) && cleanNumber.length >= 9) {
          hasCode = true;
        }
      });

      if (hasCode) {
        return "380" + cleanNumber;
      }

      return cleanNumber;
    },
    handleSearchByEnter() {
      if (this.canSearch) {
        this.findCompanies(this.newPhone.number);
      }
    },
    useMyPhone() {
      if (this.userPhone) {
        this.newPhone.number = this.userPhone;
        this.findCompanies(this.userPhone);
      }
    },
    findCompanies(number) {
      const customNumber = this.phoneReset(number);

      this.isSearching = true;
      this.searchPerformed = false;

      apiAdmin.getCard(customNumber).then((result) => {
        if (result.status == "done") {
          this.foundCompanies = result.data.сompanies || [];
          this.isSearching = false;
          this.searchPerformed = true;
        }
      });
    },
    selectFoundCompany(company) {
      this.newPhone.companyId = company.companyId;
      this.newPhone.companyName = company.companyName;
    },
    handleAddPhone() {
      const phoneNumber = this.newPhone.number.replace(/\+/g, "");

      const phoneData = {
        number: phoneNumber,
        companyId: this.newPhone.companyId,
        companyName: this.newPhone.companyName,
      };

      this.$emit("add-phone", phoneData);
      this.closeAddModal();
    },
    addPhoneWithoutCompany() {
      const phoneNumber = this.newPhone.number.replace(/\+/g, "");

      const phoneData = {
        number: phoneNumber,
        companyId: "none",
        companyName: "Компанія не додана",
      };

      this.$emit("add-phone", phoneData);
      this.closeAddModal();
    },
    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}.${month}.${year}`;
    },
    getVersionLabel(companyServer) {
      if (!companyServer) return "";

      const oldVersions = ["s-1", "online-2", "online-3", "online-4"];

      if (oldVersions.includes(companyServer)) {
        return "Стара версія";
      } else if (companyServer === "s-3") {
        return "Inside";
      } else if (companyServer === "s-0") {
        return "Root-адмінка";
      }

      return "Нова версія";
    },
    getVersionClass(companyServer) {
      if (!companyServer) return "";

      const oldVersions = ["s-1", "online-2", "online-3", "online-4"];

      if (oldVersions.includes(companyServer)) {
        return "version-old";
      } else if (companyServer === "s-3") {
        return "version-inside";
      } else if (companyServer === "s-2") {
        return "version-root";
      }

      return "version-new";
    },
    getVersionIcon(companyServer) {
      if (!companyServer) return "mdi mdi-information";

      const oldVersions = ["s-1", "online-2", "online-3", "online-4"];

      if (oldVersions.includes(companyServer)) {
        return "mdi mdi-clock-outline";
      } else if (companyServer === "s-3") {
        return "mdi mdi-office-building";
      } else if (companyServer === "s-2") {
        return "mdi mdi-shield-check";
      }

      return "mdi mdi-check-decagram";
    },
  },
};
</script>

<style scoped>
.phone-numbers-card {
  margin-bottom: 16px;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  cursor: pointer;
  font-weight: 600;
  font-size: 13px;
  color: #495057;
  position: relative;
}

.section-header > i {
  font-size: 16px;
  flex-shrink: 0;
}

.section-header > span {
  flex: 1;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

.phone-count {
  background: rgb(235 245 251);
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
}

.add-phone-btn {
  width: 28px;
  height: 28px;
  border: none;
  background: #667eea;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.add-phone-btn:hover {
  background: #556ee6;
}

.add-phone-btn i {
  font-size: 16px;
  color: white;
}

.phone-list {
  /* padding-left: 24px;
  padding-right: 8px; */
  margin-top: 8px;
  overflow: visible;
  position: relative;
}

.phone-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  margin-bottom: 6px;
  background: #f8f9fa;
  border: 2px solid #f1f3f5;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  z-index: 1;
}

.phone-item:last-child {
  margin-bottom: 0;
}

.phone-item:hover {
  border-color: #667eea;
}

.phone-item.menu-active {
  border-color: #667eea;
  background: #f8f9fe;
  transform: translateX(2px);
  z-index: 2;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.15);
}

.phone-icon {
  width: 28px;
  height: 28px;
  /* background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); */
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: pointer;
  /* color: white; */
  font-size: 14px;
  font-weight: 400;
}

.phone-content {
  flex: 1;
  cursor: pointer;
}

.phone-number {
  font-size: 14px;
  font-weight: 400;
  color: #212529;
  /* font-family: "Courier New", monospace; */
  margin-bottom: 4px;
}

.phone-company {
  font-size: 12px;
  font-weight: 300;
  color: #9fa5ab;
  display: flex;
  align-items: center;
  gap: 4px;
}

.phone-company i {
  font-size: 13px;
}

.phone-stats {
  display: flex;
  align-items: center;
  margin-left: auto;
}

.stats-info {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 3px;
}

.stats-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 3px 7px;
  background: #34c38f;
  border-radius: 12px;
  color: white;
  font-size: 10px;
  font-weight: 600;
  white-space: nowrap;
}

.stats-badge i {
  font-size: 11px;
}

.stats-date {
  font-size: 9px;
  color: #adb5bd;
  white-space: nowrap;
}

.phone-actions {
  display: flex;
  align-items: center;
  margin-left: 8px;
}

.action-btn {
  width: 32px;
  height: 32px;
  border: none;
  background: #f1f3f5;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.action-btn:hover {
  background: #e9ecef;
}

.action-btn i {
  font-size: 16px;
  color: #495057;
}

.action-menu-wrapper {
  position: relative;
}

.action-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 220px;
  padding: 6px;
  z-index: 1000;
  animation: dropdownSlide 0.2s ease;
}

@keyframes dropdownSlide {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.action-dropdown-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 13px;
  color: #495057;
}

.action-dropdown-item:hover {
  background: #f8f9fa;
}

.action-dropdown-item i {
  font-size: 16px;
  color: #667eea;
}

.action-dropdown-item span {
  font-weight: 500;
}

.action-dropdown-divider {
  height: 1px;
  background: #eff2f7;
  margin: 4px 0;
}

.action-dropdown-item-danger {
  color: #f46a6a;
}

.action-dropdown-item-danger:hover {
  background: #fef5f5;
}

.action-dropdown-item-danger i {
  color: #f46a6a;
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #adb5bd;
}

.empty-state i {
  font-size: 40px;
  color: #dee2e6;
  margin-bottom: 10px;
  display: block;
}

.empty-state p {
  margin: 0;
  font-size: 13px;
}

/* Modal styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
  animation: fadeIn 0.2s ease;
}

.modal-container {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 900px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  animation: slideUp 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.modal-header {
  padding: 20px 24px;
  border-bottom: 1px solid #eff2f7;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.modal-header-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.modal-header-content i {
  font-size: 24px;
  color: #667eea;
}

.modal-header-content h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #495057;
}

.modal-close {
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.modal-close:hover {
  background: #f8f9fa;
}

.modal-close i {
  font-size: 20px;
  color: #74788d;
}

.modal-body {
  padding: 24px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group:last-child {
  margin-bottom: 0;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #495057;
}

.form-input {
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #eff2f7;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s ease;
}

.form-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-input::placeholder {
  color: #adb5bd;
}

.modal-footer {
  padding: 16px 24px;
  border-top: 1px solid #eff2f7;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

.btn {
  padding: 8px 20px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 100px;
}

.btn-cancel {
  background: #fff;
  color: #74788d;
  border: 1px solid #eff2f7;
}

.btn-cancel:hover {
  background: #f8f9fa;
  border-color: #d6dae0;
}

.btn-primary {
  background: #667eea;
  color: white;
}

.btn-primary:hover {
  background: #556ee6;
}

.btn-primary:disabled {
  background: #adb5bd;
  cursor: not-allowed;
  opacity: 0.6;
}

.company-search-loading {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 6px;
  color: #74788d;
  font-size: 13px;
}

.company-list {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #eff2f7;
  border-radius: 8px;
  background: #fff;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  padding: 8px;
}

.company-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border: 1px solid #eff2f7;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  background: #fff;
}

.company-item:hover {
  background: #f8f9fa;
}

.company-item.selected {
  background: #f0f7ff;
  border: 2px solid #667eea;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.2);
}

.company-item.blocked {
  opacity: 0.7;
  background: #fef5f5;
  border-color: #f46a6a;
}

.company-item.blocked:hover {
  background: #fceaea;
}

.company-info {
  flex: 1;
  min-width: 0;
  padding-right: 30px;
}

.company-header {
  margin-bottom: 6px;
}

.company-name {
  font-size: 13px;
  font-weight: 600;
  color: #495057;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 3px;
  line-height: 1.3;
}

.company-admin-badge {
  font-size: 14px;
  color: #f1b44c;
}

.company-blocked-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  background: #fce8e8;
  color: #f46a6a;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
}

.company-blocked-badge i {
  font-size: 12px;
}

.company-code {
  font-size: 11px;
  color: #74788d;
  font-weight: 500;
}

.company-description {
  font-size: 11px;
  color: #74788d;
  line-height: 1.3;
  margin-bottom: 6px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  min-height: 28px;
}

.company-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.company-server-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  background: #e8f4f8;
  color: #50a5f1;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
}

.company-server-badge i {
  font-size: 12px;
}

.company-date {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #74788d;
}

.company-date i {
  font-size: 12px;
}

.company-selected-icon {
  font-size: 22px;
  color: #34c38f;
  flex-shrink: 0;
  position: absolute;
  top: 8px;
  right: 8px;
}

.company-not-found,
.company-hint {
  text-align: center;
  padding: 30px 20px;
  color: #adb5bd;
  background: #f8f9fa;
  border-radius: 6px;
}

.company-not-found i,
.company-hint i {
  font-size: 32px;
  color: #d6dae0;
  margin-bottom: 10px;
  display: block;
}

.company-not-found p,
.company-hint p {
  margin: 0;
  font-size: 13px;
  margin-bottom: 15px;
}

.btn-add-without-company {
  margin-top: 10px;
  background: #50a5f1;
  color: white;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-add-without-company:hover {
  background: #4596e0;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(80, 165, 241, 0.3);
}

.btn-add-without-company i {
  font-size: 16px;
}

.company-hint {
  color: #74788d;
}

.company-hint i {
  color: #b8bcc8;
}

.phone-input-wrapper-with-tag {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.my-phone-tag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  background: transparent;
  color: #667eea;
  border: 1px solid #d6dae0;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  align-self: flex-start;
}

.my-phone-tag:hover {
  background: #f8f9fa;
  border-color: #667eea;
  color: #556ee6;
}

.my-phone-tag:active {
  background: #eff2f7;
}

.my-phone-tag i {
  font-size: 13px;
}

.phone-input-wrapper {
  display: flex;
  gap: 8px;
  align-items: center;
}

.phone-input-wrapper .form-input {
  flex: 1;
}

.search-company-btn {
  padding: 10px 16px;
  border: 1px solid #eff2f7;
  background: #667eea;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.search-company-btn:hover:not(:disabled) {
  background: #556ee6;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.search-company-btn:disabled {
  background: #adb5bd;
  cursor: not-allowed;
  opacity: 0.6;
}

.search-company-btn i {
  font-size: 18px;
  color: white;
}

.mdi-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.companies-count {
  color: #667eea;
  font-weight: 600;
  font-size: 12px;
  background: #f0f7ff;
  padding: 2px 8px;
  border-radius: 4px;
  margin-left: 8px;
}

.company-search-input-wrapper {
  position: relative;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
}

.company-search-input-wrapper > i {
  position: absolute;
  left: 12px;
  font-size: 18px;
  color: #adb5bd;
  pointer-events: none;
}

.company-search-input {
  width: 100%;
  padding: 10px 40px 10px 40px;
  border: 2px solid #eff2f7;
  border-radius: 8px;
  font-size: 13px;
  transition: all 0.2s ease;
  background: #f8f9fa;
}

.company-search-input:focus {
  outline: none;
  border-color: #667eea;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.company-search-input::placeholder {
  color: #adb5bd;
}

.clear-search-btn {
  position: absolute;
  right: 8px;
  width: 28px;
  height: 28px;
  border: none;
  background: #e9ecef;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;
}

.clear-search-btn:hover {
  background: #d6dae0;
  transform: scale(1.05);
}

.clear-search-btn i {
  font-size: 18px;
  color: #74788d;
}

.btn-clear-filter {
  margin-top: 10px;
  background: #667eea;
  color: white;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-clear-filter:hover {
  background: #556ee6;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.btn-clear-filter i {
  font-size: 16px;
}

.company-version-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.company-version-badge i {
  font-size: 12px;
}

.company-version-badge.version-old {
  background: #f065481a;
  color: #f06548;
  border: 1px solid #f065481a;
}

.company-version-badge.version-new {
  background: #fff4e5;
  color: #f1b44c;
  border: 1px solid #fff4e5;
}

.company-version-badge.version-inside {
  background: #00000040;
  color: #000000;
  border: 1px solid #00000040;
}

.company-version-badge.version-root {
  background: #f3e5f5;
  color: #9c27b0;
  border: 1px solid #e1bee7;
}

@media (max-width: 1024px) {
  .company-list {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .company-list {
    grid-template-columns: 1fr;
  }

  .modal-container {
    max-width: 100%;
  }

  .section-header {
    font-size: 12px;
    padding: 6px 0;
  }

  .phone-item {
    padding: 10px;
  }

  .phone-stats {
    align-items: center;
  }
}
</style>
